// Ported from dev-scrips-pm-react src/patients/components/chart/chart-tab-body.tsx (PROD-1119 Patients UI port).
/**
 * ChartTabBody — inner-tabbed Chart shell (PROD-735).
 *
 * Tab 3 of the patient-edit shell. Mirrors Flutter `ChartMainView`
 * (`patient_module/patient_chart/.../chart_main_view.dart`).
 *
 * **Inner tabs (12, in Flutter order):**
 *   Allergies · Problems · Procedures · Medications · D/I (Drug Interactions) ·
 *   Immunizations · Labs · Documents · Appointments · Notes · Flags · History
 *
 * Demog is intentionally hidden in the patient-edit context (Flutter
 * `create_patient_screen.dart:1170` passes `showDemogTab: false`) since
 * Personal Details is already its own top-level tab.
 *
 * **Visibility flags** — Flutter exposes `showMeds`, `showDITab`,
 * `showAppts`, `showNotes`, `showFlags`. From the patient-edit shell all
 * default to true. The `visibility` prop is wired now so a follow-up
 * ticket can flip individual flags off (e.g. tenants without the meds
 * feature) without touching this component. Samer PR #152 review.
 *
 * **Active inner tab persistence**: read from `?inner=<key>` so a deep
 * link survives reloads. Defaults to "allergies". The Chart-scoped
 * `inner=` query param is stripped automatically when the user switches
 * to a different top-level tab — see `patient-edit-page.tsx`.
 *
 * **Falsy `patientId` handling** — both `null` (create-mode, patient not
 * saved) and `undefined` (parent's patient query still resolving)
 * collapse to the SAME safe placeholder. There is no loading-skeleton
 * variant: the parent shell's top-bar already shows a skeleton during
 * the initial fetch, and an inner skeleton here would double-render the
 * spinner. Differentiating the two falsy states would only matter if
 * we wanted a "patient is loading" sub-state; the parent reseats this
 * component once the patient resolves so transient `undefined` is
 * extremely short-lived. Samer PR #152 review 2026-05-15.
 *
 * **PR scope (PROD-735)**: shell only. Each inner tab renders a
 * `TabPlaceholder` until its owning ticket lands:
 *   - PROD-736: Allergies, Problems, Procedures, Immunizations, Flags, History
 *   - PROD-737: Medications, D/I
 *   - PROD-738: Labs, Documents, Notes (+ E-prescription, Timeline as
 *               separate surfaces outside the inner-tab strip)
 *
 * Appointments inner-tab renders its own read-only Future/Past card list
 * (`AppointmentsChartTab`, Flutter `appts_tab.dart`) — a simpler surface
 * than the filterable table on the top-level Appointments tab
 * (`patient_appointments_tab.dart`). Both exist in Flutter; we keep both.
 */

import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { SubTabs, type SubTabDef } from '@scripsteam/scrips-signal-ds';
import { TabPlaceholder } from '@/patients/tabs/tab-placeholder';
import { CHART_INNER_PARAM, CHART_TAB_PARAM } from '@/patients/lib/patient-edit-tabs';
import { PatientAllergiesTab } from './allergies-chart-tab';
import { ProblemsChartTab } from './problems-chart-tab';
import { ProceduresChartTab } from './procedures-chart-tab';
import { ImmunizationsChartTab } from './immunizations-chart-tab';
import { FlagsChartTab } from './flags-chart-tab';
import { HistoryChartTab } from './history-chart-tab';
import { MedicationsChartTab } from './medications-chart-tab';
import { DrugInteractionsChartTab } from './drug-interactions-chart-tab';
import { LabsChartTab } from './labs-chart-tab';
import { DocumentsWithInbox } from '@/intake/components/documents-with-inbox';
import { NotesChartTab } from './notes-chart-tab';
import { AppointmentsChartTab } from './appointments-chart-tab';
import { DemogChartTab } from './demog-chart-tab';
import { ChartLeftPanel } from './chart-left-panel';
import { PatientChartPage, type ChartPageLayout, type ChartPageTab } from './patient-chart-page';
import { ChartMeta } from './chart-meta';
import { ChartOverview } from './chart-overview';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { ChartTimeline } from './chart-timeline';
import { usePatientChartRealtime } from '@/patients/realtime/use-patient-chart-realtime';
import type { Patient } from '@/types/patient-api';

const INNER_TAB_KEYS = [
  'demog',
  'allergies',
  'problems',
  'procedures',
  'medications',
  'drug-interactions',
  'immunizations',
  'labs',
  'documents',
  'appointments',
  'notes',
  'flags',
  'history',
] as const;

type InnerTabKey = (typeof INNER_TAB_KEYS)[number];

interface InnerTab {
  key: InnerTabKey;
  label: string;
  jiraKey: 'PROD-736' | 'PROD-737' | 'PROD-738' | 'PROD-734' | 'PROD-1988';
  /** Visibility flag this tab is gated on. `undefined` → always visible. */
  visibilityKey?: keyof ChartVisibilityFlags;
}

const INNER_TABS: InnerTab[] = [
  // FIRST in Flutter's list (chart_main_view.dart:222-241) and gated on the same flag:
  // the ENCOUNTER mount leaves `showDemogTab` at its default true, the patient-edit
  // mount passes false (create_patient_screen.dart:1175) because that shell already has
  // its own Personal/Contact tabs. PROD-1988.
  { key: 'demog', label: 'Demog', jiraKey: 'PROD-1988', visibilityKey: 'demographics' },
  { key: 'allergies', label: 'Allergies', jiraKey: 'PROD-736' },
  { key: 'problems', label: 'Problems', jiraKey: 'PROD-736' },
  { key: 'procedures', label: 'Proc', jiraKey: 'PROD-736' },
  {
    key: 'medications',
    label: 'Meds',
    jiraKey: 'PROD-737',
    visibilityKey: 'medications',
  },
  {
    key: 'drug-interactions',
    label: 'D/I',
    jiraKey: 'PROD-737',
    visibilityKey: 'drugInteractions',
  },
  { key: 'immunizations', label: 'Vax', jiraKey: 'PROD-736' },
  { key: 'labs', label: 'Labs', jiraKey: 'PROD-738' },
  { key: 'documents', label: 'Docs', jiraKey: 'PROD-738' },
  {
    key: 'appointments',
    label: 'Appts',
    jiraKey: 'PROD-734',
    visibilityKey: 'appointments',
  },
  {
    key: 'notes',
    label: 'Notes',
    jiraKey: 'PROD-738',
    visibilityKey: 'notes',
  },
  {
    key: 'flags',
    label: 'Flags',
    jiraKey: 'PROD-736',
    visibilityKey: 'flags',
  },
  { key: 'history', label: 'History', jiraKey: 'PROD-736' },
];

/**
 * Per-tab visibility flags. Match Flutter's `ChartMainView` constructor:
 * `showMeds`, `showDITab`, `showAppts`, `showNotes`, `showFlags`. Tabs
 * with no flag (Allergies, Problems, Procedures, Immunizations, Labs,
 * Documents, History) are always visible.
 *
 * All default to `true` — the patient-edit shell sends every flag on.
 * A future ticket can flip individual flags off (tenants without the
 * meds feature, role-gated D/I surface, etc.) without modifying this
 * component.
 */
export interface ChartVisibilityFlags {
  /** Flutter `showDemogTab`. Defaults to FALSE here, inverting Flutter's own default,
   *  because this component's first host was the patient-edit shell — the one mount that
   *  passes `showDemogTab: false`. The encounter mount opts in. (PROD-1988) */
  demographics?: boolean;
  medications?: boolean;
  drugInteractions?: boolean;
  appointments?: boolean;
  notes?: boolean;
  flags?: boolean;
}

/** The tabs of the tabbed chart (Antonin, 19 Oct: "tabs like the prototype"), in the prototype's order. Each names the
 *  inner sections it shows, in order; Drug interactions sit inside Medications, Allergies beside Problems. */
const OVERVIEW_TAB = 'overview';
/** `?x=chart-timeline` (dev default ON): the lead tab is the Timeline, in place of the Overview. */
const TIMELINE_TAB = 'timeline';

const TAB_LABELS: Record<string, string> = {
  [OVERVIEW_TAB]: 'Overview',
  [TIMELINE_TAB]: 'Timeline',
  demog: 'Demographics',
  documents: 'Documents',
  medications: 'Medications',
  problems: 'Problems & allergies',
  labs: 'Labs',
  immunizations: 'Vaccinations',
  procedures: 'Procedures',
  appointments: 'Appointments',
  notes: 'Notes',
  flags: 'Flags',
  history: 'History',
  'drug-interactions': 'Drug interactions',
};

/** The titles the page draws over a section (the tab strip's labels are abbreviated). */
const PART_TITLES: Record<InnerTabKey, string> = {
  demog: 'Demographics',
  allergies: 'Allergies',
  problems: 'Problems',
  procedures: 'Procedures',
  medications: 'Medications',
  'drug-interactions': 'Drug interactions',
  immunizations: 'Vaccinations',
  labs: 'Labs',
  documents: 'Documents',
  appointments: 'Appointments',
  notes: 'Notes',
  flags: 'Flags',
  history: 'History',
};

/** An old `?inner=` deep link names a section; it opens the tab that now holds it. */
const LEGACY_INNER_TAB: Partial<Record<string, string>> = {
  allergies: 'problems',
  'drug-interactions': 'medications',
};

const PANEL_FOLD_KEY = 'chart-left-panel';

/** Where the patient panel starts open (the bridge's `--chart-panel-open-from`, 1440): narrower windows start it folded. */
function panelOpenFrom(): number {
  const raw = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--chart-panel-open-from'));
  return Number.isFinite(raw) ? raw : 1440;
}

/** The reader's own choice (the ‹ toggle) wins; with none yet, folded below the breakpoint, open from it. */
function readPanelFolded(): boolean {
  try {
    const saved = window.localStorage.getItem(PANEL_FOLD_KEY);
    if (saved === 'folded') return true;
    if (saved === 'open') return false;
  } catch {
    /* a blocked store: no saved choice */
  }
  return window.innerWidth < panelOpenFrom();
}

const DEFAULT_VISIBILITY: Required<ChartVisibilityFlags> = {
  demographics: false,
  medications: true,
  drugInteractions: true,
  appointments: true,
  notes: true,
  flags: true,
};

export interface ChartTabBodyProps {
  /** `?x=chart-timeline`: the lead tab may be the Timeline. False keeps the Overview whatever the switch says. Default true. */
  timeline?: boolean;
  patient: Patient | null;
  /** Optional per-tab visibility overrides. Each flag defaults to `true`. */
  visibility?: ChartVisibilityFlags;
  /**
   * Encounter this chart is mounted from (PROD-1986). Flutter's ENCOUNTER mount passes
   * `encounterId` into ChartMainView (chart_main_view.dart:260-495 threads it into ~8 tabs); the
   * patient-edit mount (create_patient_screen.dart:1170) passes none.
   *
   * It stamps new uploads onto the encounter. It does NOT scope the Docs LIST: the chart's
   * Docs tab shows the patient's full document history on BOTH mounts, because Flutter's
   * chart `doc_tab.dart:619-638 fetchDocuments()` sends no encounterId. (An earlier port
   * read `chart_main_view.dart:406` passing the id to DocTab as list scoping; it is the
   * add-path stamp.) The encounter-scoped list is the encounter's own Docs STEP.
   */
  encounterId?: string | null;
  /** The chart as one page with horizontal tabs and a summary line over them (Overview · Documents · Medications …;
   *  design log 19 Oct, replacing the one page with a still index of 15 Oct). Both hosts (patient record, encounter)
   *  pass it; the inner-tab arrangement stays for stories and tests. */
  onePage?: boolean;
  /** Tabbed page only: draw the Overview tab. Default true. */
  summary?: boolean;
  /** Tabbed page only: draw the patient panel (with its fold toggle) on the left. Default: as `summary`. The encounter's
   *  started frame already owns a left panel on every tab, so its host turns this off and the page shows ONE panel. */
  leftPanel?: boolean;
  /** Tabbed page only: `stack` (default; the encounter's chart pane) or `track` (the patient record's Chart tab: a fixed
   *  head over a horizontal track of columns, the Overview first; Antonin's export 21 Oct). */
  layout?: ChartPageLayout;
}

export function ChartTabBody({
  patient,
  visibility,
  encounterId,
  onePage = false,
  summary = true,
  timeline = true,
  leftPanel = summary,
  layout = 'stack',
}: ChartTabBodyProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const flags = { ...DEFAULT_VISIBILITY, ...(visibility ?? {}) };

  // Wire the patient-encounter SignalR hub. Subscribes to
  // `PatientePrescription` (typo preserved) so meds + D/I queries auto-
  // refresh when another user prescribes / stops a medication. Lifecycle
  // is bound to the patient — switching to a different patient stops the
  // current connection and starts a fresh one for the new id.
  usePatientChartRealtime(patient?.patientId ?? null);

  // The patient panel's fold (the ‹ toggle), kept per browser. Folded, the panel is gone and the page takes the room.
  const [panelFolded, setPanelFolded] = useState(readPanelFolded);
  // `?x=chart-timeline` (dev default ON): the lead tab is the Timeline, in place of the Overview. Read once per mount.
  const [timelineFlag] = useState(() => isEncounterFlagOn('chartTimeline'));
  const timelineOn = timeline && timelineFlag;
  const togglePanel = () =>
    setPanelFolded((folded) => {
      try {
        window.localStorage.setItem(PANEL_FOLD_KEY, folded ? 'open' : 'folded');
      } catch {
        /* a blocked store keeps the choice for this visit only */
      }
      return !folded;
    });

  const visibleTabs = INNER_TABS.filter((t) =>
    t.visibilityKey ? flags[t.visibilityKey] : true,
  );

  // Flutter opens the chart on `initialIndex: 0` (chart_main_view.dart:140) — the FIRST
  // tab of the visible set, which is Demog where it shows and Allergies where it does
  // not. So the landing tab is derived, not hardcoded (PROD-1988).
  const landingTab: InnerTabKey = visibleTabs[0]?.key ?? 'allergies';
  const requested = (searchParams.get(CHART_INNER_PARAM) ?? landingTab) as InnerTabKey;
  // Validate against the visible set, not the global set — if a flag was
  // flipped off, an old deep link should fall back to the landing tab.
  const active: InnerTabKey = visibleTabs.some((t) => t.key === requested)
    ? requested
    : landingTab;

  const handleChange = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === landingTab) {
      params.delete(CHART_INNER_PARAM);
    } else {
      params.set(CHART_INNER_PARAM, next);
    }
    setSearchParams(params, { replace: false });
  };

  if (!patient?.patientId) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-dashed border-border-default p-8 text-center">
          <p className="text-sm text-text-muted">
            Save the patient first to view their chart.
          </p>
        </div>
      </div>
    );
  }

  const subTabs: SubTabDef[] = visibleTabs.map((t) => ({
    id: t.key,
    label: t.label,
    panelId: `chart-panel-${t.key}`,
  }));

  const renderTabBody = (key: InnerTabKey) => {
    switch (key) {
      case 'demog':
        return <DemogChartTab patient={patient} />;
      case 'appointments':
        return <AppointmentsChartTab patientId={patient.patientId} />;
      case 'allergies':
        return <PatientAllergiesTab patientId={patient.patientId} />;
      case 'problems':
        return <ProblemsChartTab patientId={patient.patientId} />;
      case 'procedures':
        return <ProceduresChartTab patientId={patient.patientId} />;
      case 'immunizations':
        return <ImmunizationsChartTab patientId={patient.patientId} patient={patient} />;
      case 'flags':
        return <FlagsChartTab patientId={patient.patientId} />;
      case 'history':
        return <HistoryChartTab patientId={patient.patientId} />;
      case 'medications':
        return <MedicationsChartTab patientId={patient.patientId} />;
      case 'drug-interactions':
        return <DrugInteractionsChartTab patientId={patient.patientId} />;
      case 'labs':
        return <LabsChartTab patientId={patient.patientId} />;
      case 'documents':
        return (
          // PROD-3274: with VITE_DOCUMENT_INTAKE off this is DocumentsChartTab alone, as before.
          <DocumentsWithInbox
            patientFirstName={patient.firstName}
            patientId={patient.patientId}
            // Stamp only — no `listScope`, so the chart's Docs tab lists the patient's
            // FULL document history on both mounts, exactly as Flutter's chart
            // `doc_tab.dart:619-638 fetchDocuments()` does (it sends no encounterId).
            encounterId={encounterId}
          />
        );
      case 'notes':
        return <NotesChartTab patientId={patient.patientId} patient={patient} />;
      default: {
        const t = visibleTabs.find((vt) => vt.key === key)!;
        return (
          <TabPlaceholder
            jiraKey={t.jiraKey}
            title={t.label}
            subtitle={subtitleFor(t.jiraKey)}
          />
        );
      }
    }
  };

  if (onePage) {
    const patientId = patient.patientId;
    const has = (key: InnerTabKey) => visibleTabs.some((t) => t.key === key);
    const part = (key: InnerTabKey) => ({ key, title: PART_TITLES[key] });
    const tabs: ChartPageTab[] = [
      ...(summary
        ? [
            timelineOn
              ? {
                  key: TIMELINE_TAB,
                  label: TAB_LABELS[TIMELINE_TAB],
                  lead: true,
                  render: () => <ChartTimeline patientId={patientId} />,
                }
              : {
                  key: OVERVIEW_TAB,
                  label: TAB_LABELS[OVERVIEW_TAB],
                  lead: true,
                  render: () => <ChartOverview patientId={patientId} onOpen={(target) => openTab(target)} />,
                },
          ]
        : []),
      ...(has('demog') ? [{ key: 'demog', label: TAB_LABELS.demog, parts: [part('demog')] }] : []),
      ...(has('documents') ? [{ key: 'documents', label: TAB_LABELS.documents, parts: [part('documents')] }] : []),
      ...(has('medications')
        ? [
            {
              key: 'medications',
              label: TAB_LABELS.medications,
              parts: [part('medications'), ...(has('drug-interactions') ? [part('drug-interactions')] : [])],
              countParts: ['medications'],
            },
          ]
        : has('drug-interactions')
          ? [{ key: 'drug-interactions', label: TAB_LABELS['drug-interactions'], parts: [part('drug-interactions')] }]
          : []),
      { key: 'problems', label: TAB_LABELS.problems, parts: [part('problems'), part('allergies')] },
      ...(['labs', 'immunizations', 'procedures', 'appointments', 'notes', 'flags', 'history'] as const)
        .filter(has)
        .map((k) => ({ key: k, label: TAB_LABELS[k], parts: [part(k)] })),
    ];
    const tabKeys = tabs.map((t) => t.key);
    const legacy = LEGACY_INNER_TAB[searchParams.get(CHART_INNER_PARAM) ?? ''] ?? searchParams.get(CHART_INNER_PARAM);
    const wanted = searchParams.get(CHART_TAB_PARAM) ?? legacy ?? tabKeys[0];
    const activeTabKey = tabKeys.includes(wanted ?? '') ? (wanted as string) : tabKeys[0];
    function openTab(key: string) {
      const params = new URLSearchParams(searchParams);
      params.delete(CHART_INNER_PARAM);
      if (key === tabKeys[0]) params.delete(CHART_TAB_PARAM);
      else params.set(CHART_TAB_PARAM, key);
      // Pushed, so Back returns to the tab the reader came from and a refresh keeps this one.
      setSearchParams(params, { replace: false });
    }
    const page = (
      <PatientChartPage
        tabs={tabs}
        active={activeTabKey}
        onActiveChange={openTab}
        meta={<ChartMeta patientId={patientId} />}
        renderPart={(key) => renderTabBody(key as InnerTabKey)}
        layout={layout}
      />
    );
    // The prototype's layout: the patient panel on the left, the page beside it. A host that already draws its own
    // left panel (the encounter's started frame) passes `leftPanel={false}` and keeps the page alone.
    return (
      <div data-slot="patient-chart" data-testid="patient-chart-page-root">
        {leftPanel ? (
          <div data-slot="chart-with-panel" data-folded={panelFolded ? 'true' : undefined}>
            <aside data-slot="chart-left-panel" aria-label="Patient summary" inert={panelFolded}>
              <ChartLeftPanel patient={patient} />
            </aside>
            <button
              type="button"
              data-slot="chart-panel-toggle"
              aria-expanded={!panelFolded}
              aria-label={panelFolded ? 'Show patient summary' : 'Hide patient summary'}
              onClick={togglePanel}
            >
              {panelFolded ? <ChevronRight aria-hidden /> : <ChevronLeft aria-hidden />}
            </button>
            <div data-slot="chart-main">{page}</div>
          </div>
        ) : (
          page
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0 flex flex-col">
        <SubTabs
          tabs={subTabs}
          value={active}
          onValueChange={(v) => handleChange(v)}
          className="px-6 overflow-x-auto scrollbar-hide"
        />

        <div
          id={`chart-panel-${active}`}
          role="tabpanel"
          aria-labelledby={`sub-tab-${active}`}
          className="overflow-auto scrollbar-hide flex-1 min-h-0"
        >
          {renderTabBody(active)}
        </div>
      </div>
    </div>
  );
}

function subtitleFor(jiraKey: InnerTab['jiraKey']): string {
  switch (jiraKey) {
    case 'PROD-736':
      return 'Simple CRUD tab — ships under PROD-736.';
    case 'PROD-737':
      return 'Medication management + drug interaction surface — ships under PROD-737.';
    case 'PROD-738':
      return 'Long-form clinical surface — ships under PROD-738.';
    case 'PROD-734':
      // Appointments renders AppointmentsChartTab directly (never the
      // placeholder); kept for switch exhaustiveness.
      return 'Appointments — Future / Past card list.';
    case 'PROD-1988':
      // Demog renders DemogChartTab directly (never the placeholder); kept for
      // switch exhaustiveness.
      return 'Demographics — the Flutter chart Demog tab.';
  }
}
